/**
 * EditableJourney.tsx
 *
 * A presentation component for rendering and editing journeys.
 * 
 * This component is a thin wrapper that uses the useJourneyEditor hook for all
 * business logic and focuses purely on rendering the UI. It supports only the
 * v2 journey format for simplicity and maintainability.
 * 
 * Key features:
 * - View and edit modes
 * - Add, remove, and replace stops
 * - Update transport modes
 * - Submit changes for AI processing
 * - Approve changes (update existing or create new journey)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useJourneyEdit, useJourneyEditActions } from '../contexts/JourneyEditContext';
import { useJourneyEditor } from '../features/journey-editor/hooks/useJourneyEditor';
import JourneyStop from './JourneyStop';
import Badge from './ui/Badge';
import Button from './ui/button';
import Select from './ui/Select';
import EnvironmentalImpactCard from './EnvironmentalImpactCard';
import JourneyTotalsCards from './JourneyTotalsCards';
import LLMLoadingSpinner from './LLMLoadingSpinner';
import { getTransportPreferenceEmoji, getTransportEmoji } from '../shared/transport';
import { formatDistance } from '../shared/formatters';
import StopLocationDialog from '../features/journey-editor/components/dialogs/StopLocationDialog';
import RemoveStopDialog from '../features/journey-editor/components/dialogs/RemoveStopDialog';
import { EditActionBar } from '../features/journey-editor/components/EditActionBar';
import ConfirmationDialog from './ConfirmationDialog';
import InfoDialog from './InfoDialog';

// Import leg and stop utilities from the journey-editor module
import {
  getLegToStop,
  getTransportToStop,
  getEffectiveTransportMode as getEffectiveTransportModeUtil,
  isLegOutdated,
} from '../features/journey-editor/utils/legUtils';
import {
  isStopNew as isStopNewUtil,
  getJourneyTotals,
} from '../features/journey-editor/utils/stopUtils';

/** Transport mode options for the dropdown */
const transportOptions = [
  { value: 'train', label: 'Train 🚆' },
  { value: 'bus', label: 'Bus 🚌' },
  { value: 'airplane', label: 'Airplane ✈️' },
  { value: 'car_sharing', label: 'Car Sharing 🚗' },
  { value: 'boat', label: 'Boat ⛴️' },
  { value: 'car', label: 'Car 🚗' },
  { value: 'motorcycle', label: 'Motorcycle 🏍️' },
  { value: 'bicycle', label: 'Bicycle 🚴' },
  { value: 'auto', label: 'Let Jody decide' },
];

/**
 * Props for the EditableJourney component.
 */
interface EditableJourneyProps {
  /** Callback when the "Save Journey" button is clicked (view mode) */
  onSaveJourney?: () => void;
  /** Callback after a journey is successfully saved */
  onJourneySaved?: (saved: any) => void;
  /** Register an external discard handler (for drawer close confirmation) */
  registerExternalDiscard?: (fn: () => void) => void;
}

interface EditableJourneyInternalProps extends EditableJourneyProps {
  journey?: any;
}

/**
 * EditableJourney component.
 * 
 * A thin presentation layer for journey editing that delegates all business
 * logic to the useJourneyEditor hook.
 */
const EditableJourney: React.FC<EditableJourneyInternalProps> = ({
  journey,
  onJourneySaved,
  registerExternalDiscard
}) => {
  const { state } = useJourneyEdit();
  const actions = useJourneyEditActions();
  const editor = useJourneyEditor();
  
  // Local UI state for dialogs
  const [addingStopAfter, setAddingStopAfter] = useState<number | null>(null);
  const [replacingStopId, setReplacingStopId] = useState<string | null>(null);
  const [removingStopId, setRemovingStopId] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [editingTransportLegId, setEditingTransportLegId] = useState<string | null>(null);
  
  // Animation state
  const [fadingOutStops, setFadingOutStops] = useState<Set<string>>(new Set());
  
  const { currentJourney, mode, awaitingApproval, pendingChanges } = editor;
  
  // Track loaded journey to prevent infinite loops
  const loadedJourneyRef = useRef<string | null>(null);
  
  // Load journey into context when journey prop changes
  useEffect(() => {
    if (journey) {
      const journeyId = (journey as any).id || JSON.stringify(journey);
      // Only load if it's a different journey
      if (journeyId !== loadedJourneyRef.current && !state.awaitingApproval) {
        console.log('[EditableJourney] Loading journey into context:', journeyId);
        actions.loadJourney(journey as any);
        loadedJourneyRef.current = journeyId;
      }
    }
  }, [journey, state.awaitingApproval, actions]);
  
  // Register external discard handler for drawer
  useEffect(() => {
    if (registerExternalDiscard) {
      registerExternalDiscard(() => {
        if (pendingChanges.length > 0) {
          setShowDiscardDialog(true);
          return false; // Prevent close
        }
        return true; // Allow close
      });
    }
  }, [registerExternalDiscard, pendingChanges.length]);
  
  // No journey to display
  if (!currentJourney) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        No journey data available.
      </div>
    );
  }
  
  // Validate journey structure
  if (!currentJourney.stops || !Array.isArray(currentJourney.stops)) {
    console.error('[EditableJourney] Invalid journey structure:', currentJourney);
    return (
      <div className="flex items-center justify-center p-8 text-red-500">
        Invalid journey data. Please refresh the page.
      </div>
    );
  }
  
  const stops = currentJourney.stops || [];
  const totals = getJourneyTotals(currentJourney);
  const transportPref = (currentJourney as any).transportPreference || (currentJourney as any).transport_preference || 'none';
  const prefIcon = getTransportPreferenceEmoji(transportPref);
  const showActionBar = mode === 'edit' || awaitingApproval;
  
  // Helper wrappers for utility functions (to pass current journey and pending changes)
  const getEffectiveTransportMode = (stopId: string): string => 
    getEffectiveTransportModeUtil(currentJourney, stopId, pendingChanges);
  
  const isStopValueOutdated = (stopId: string): boolean => 
    isLegOutdated(currentJourney, stopId, pendingChanges);
  
  const isStopNew = (stopId: string): boolean => 
    isStopNewUtil(stopId, pendingChanges);
  
  // Handler: Add stop
  const handleAddStop = (afterOrder: number) => {
    if (awaitingApproval) return;
    setAddingStopAfter(afterOrder);
  };
  
  const confirmAddStop = (
    method: 'manual' | 'ai_assisted',
    city?: string,
    country?: string,
    instructions?: string
  ) => {
    if (addingStopAfter === null) return;
    
    // afterOrder of 0 means "insert before the first stop" (at position 1)
    // afterOrder of N means "insert after stop N" (at position N+1)
    editor.addStop(addingStopAfter, method, city, country, instructions);
    setAddingStopAfter(null);
  };
  
  // Handler: Replace stop
  const handleReplaceStop = (stopId: string) => {
    if (awaitingApproval) return;
    setReplacingStopId(stopId);
  };
  
  const confirmReplaceStop = (
    method: 'manual' | 'ai_assisted',
    city?: string,
    country?: string,
    instructions?: string
  ) => {
    if (!replacingStopId) return;
    
    editor.replaceStop(replacingStopId, method, city, country, instructions);
    setReplacingStopId(null);
  };
  
  // Handler: Remove stop
  const handleRemoveStop = (stopId: string) => {
    if (awaitingApproval) return;
    setRemovingStopId(stopId);
  };
  
  const confirmRemoveStop = () => {
    if (!removingStopId) return;
    
    // Start fade out animation
    setFadingOutStops(prev => new Set(prev).add(removingStopId));
    
    // After animation, remove the stop
    setTimeout(() => {
      editor.removeStop(removingStopId);
      setFadingOutStops(prev => {
        const newSet = new Set(prev);
        newSet.delete(removingStopId);
        return newSet;
      });
      setRemovingStopId(null);
    }, 300);
  };
  
  // Handler: Transport change
  const handleTransportChange = (stopId: string, newTransportMode: string) => {
    if (awaitingApproval) return;
    
    const leg = getLegToStop(currentJourney, stopId);
    if (!leg?.id) {
      console.error('[EditableJourney] Cannot change transport: leg not found');
      return;
    }
    
    editor.updateLegTransport(leg.id, newTransportMode);
  };
  
  // Handler: Discard changes
  const handleDiscardChanges = () => {
    setShowDiscardDialog(true);
  };
  
  const confirmDiscardChanges = () => {
    editor.discardChanges();
    setShowDiscardDialog(false);
  };
  
  // Approval handlers with callbacks
  const handleApproveUpdate = async () => {
    const result = await editor.approveUpdate();
    if (result && onJourneySaved) {
      onJourneySaved(result);
    }
  };
  
  const handleApproveCreateNew = async () => {
    const result = await editor.approveCreateNew();
    if (result && onJourneySaved) {
      onJourneySaved(result);
    }
  };
  
  const handleApproveCreateUnsaved = async () => {
    const result = await editor.approveCreateUnsaved();
    if (result && onJourneySaved) {
      onJourneySaved(result);
    }
  };
  
  return (
    <div className="flex flex-col gap-4 pb-20">
      {/* Show loading spinner as fullscreen overlay while submitting changes */}
      <LLMLoadingSpinner
        message="Reworking your journey…"
        onCancel={editor.cancelSubmitChanges}
        isVisible={editor.isSubmitting}
      />

      {/* Journey Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge icon="📋">{(currentJourney as any).status || 'DRAFT'}</Badge>
          {prefIcon && (
            <Badge icon={prefIcon}>Transport Pref</Badge>
          )}
          {mode === 'edit' && (
            <Badge icon="✏️">Editing</Badge>
          )}
        </div>
      </div>
      
      {/* Journey Metrics */}
      <JourneyTotalsCards
        totalCost={totals.cost}
        totalCo2={totals.co2}
        totalDistance={totals.distance}
      />

      <EnvironmentalImpactCard
        distanceKm={totals.distance}
        co2Kg={totals.co2}
        formattedDistance={formatDistance(totals.distance)}
      />
      
      {/* Route Section */}
      <div>
        <div className="text-[var(--text)] font-semibold mb-2 flex items-center gap-2">🗺️ Your Route</div>
        <div className="relative">
          {/* Vertical timeline */}
          <div className="absolute left-3 top-4 bottom-3 w-[2px] bg-[var(--border)]" />
          
          <div className="space-y-4">
            {stops.map((stop: any, index: number) => {
              const transport = getTransportToStop(currentJourney, stop.id);
              const effectiveTransportMode = getEffectiveTransportMode(stop.id);
              const isOutdated = isStopValueOutdated(stop.id);
              const isNew = isStopNew(stop.id);
              const isFading = fadingOutStops.has(stop.id);
              
              // Build stop object with transport data for display
              const stopWithTransport = {
                ...stop,
                transport_mode_from_previous: effectiveTransportMode || transport.mode,
                distance_from_previous_km: transport.distance,
                transport_time_from_previous_hours: transport.duration,
                transport_cost_eur: transport.cost,
                transport_co2e_kg_from_previous: transport.co2,
                has_outdated_transport: isOutdated,
              };
              
              // Get location for display
              const stopLocation = (stop as any).city || (stop as any).location || 'Unknown';
              const nextStop = index < stops.length - 1 ? stops[index + 1] : null;
              const nextStopLocation = nextStop ? ((nextStop as any).city || (nextStop as any).location || 'Unknown') : null;
              
              const approvalTooltip = awaitingApproval ? 'Approve or discard to continue editing' : undefined;
              
              return (
                <div key={stop.id || `stop-${index}`} className="relative">
                  {/* Add Stop Button (before first stop) */}
                  {index === 0 && (
                    <div className="flex justify-center mb-2">
                      <Button
                        variant="secondary"
                        onClick={() => handleAddStop(0)}
                        disabled={awaitingApproval || editor.isSubmitting}
                        title={approvalTooltip}
                        className="text-xs px-3 py-1"
                      >
                        <span className="mr-1">➕</span>
                        Add new stop
                      </Button>
                    </div>
                  )}

                  {/* Stop badge on timeline */}
                  <div 
                    className="absolute left-0 top-4 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold z-10"
                    style={{ backgroundColor: '#374151' }}
                  >
                    {stop.order}
                  </div>

                  {/* Journey Stop */}
                  <div 
                    className="relative"
                    style={{
                      opacity: isFading ? 0 : 1,
                      transform: isFading ? 'scale(0.95)' : 'scale(1)',
                      transition: isFading ? 'opacity 800ms ease-out, transform 800ms ease-out' : 'none',
                    }}
                  >
                    {/* Edit Controls - buttons in top-right corner of stop */}
                    <div className="absolute right-2 top-2 z-20 flex gap-1">
                      {/* Replace/Edit Stop Button */}
                      <Button
                        variant="secondary"
                        onClick={() => handleReplaceStop(stop.id)}
                        disabled={awaitingApproval || editor.isSubmitting}
                        className="p-1 h-6 w-6 text-xs bg-transparent hover:bg-gray-100 border-gray-300 shadow-sm"
                        title={approvalTooltip || 'Change this stop'}
                      >
                        <span>✏️</span>
                      </Button>
                      
                      {/* Remove Stop Button - Only if more than 2 stops */}
                      {stops.length > 2 && (
                        <Button
                          variant="secondary"
                          onClick={() => handleRemoveStop(stop.id)}
                          disabled={awaitingApproval || editor.isSubmitting}
                          className="p-1 h-6 w-6 text-xs bg-transparent hover:bg-red-100 border-gray-300 shadow-sm"
                          title={approvalTooltip || 'Remove this stop'}
                        >
                          <span className="text-red-600">✕</span>
                        </Button>
                      )}
                    </div>

                    <JourneyStop
                      stop={stopWithTransport}
                      isFirst={index === 0}
                      isNew={isNew}
                      disableNights={awaitingApproval || isNew}
                    />
                  </div>
                  
                  {/* Leg Element: Transport details between stops */}
                  {nextStop && nextStop.id && (() => {
                    const nextStopId = nextStop.id as string;
                    const nextTransport = getTransportToStop(currentJourney, nextStopId);
                    const nextIsOutdated = isStopValueOutdated(nextStopId);
                    const nextEffectiveMode = getEffectiveTransportMode(nextStopId) || nextTransport.mode || 'auto';
                    const isEditingThisLeg = editingTransportLegId === nextStopId;
                    
                    return (
                      <div className="ml-8 mr-0 mt-2 mb-4 relative">
                        <div 
                          className={`bg-[var(--surface-alt)] rounded-md p-3 border-l-4 ${nextIsOutdated ? 'border-orange-400 bg-orange-50' : ''}`} 
                          style={{ borderLeftColor: nextIsOutdated ? '#fb923c' : '#374151' }}
                        >
                          {/* Edit Transport Button */}
                          <div className="absolute right-2 top-2 z-20">
                            <Button
                              variant="secondary"
                              onClick={() => setEditingTransportLegId(isEditingThisLeg ? null : nextStopId)}
                              disabled={awaitingApproval || editor.isSubmitting}
                              className="p-1 h-6 w-6 text-xs bg-transparent hover:bg-gray-100 border-gray-300 shadow-sm"
                              title={awaitingApproval ? 'Approve or discard to continue editing' : 'Change transport mode'}
                            >
                              <span>✏️</span>
                            </Button>
                          </div>

                          {/* Route label */}
                          <div className="mb-2">
                            <label className="block text-xs font-medium text-[var(--text-muted)]">
                              {stopLocation} → {nextStopLocation}
                            </label>
                            
                            {/* Transport Mode Dropdown - visible when editing this leg */}
                            {isEditingThisLeg && (
                              <Select
                                value={nextEffectiveMode}
                                onChange={(e) => {
                                  handleTransportChange(nextStopId, e.target.value);
                                  setEditingTransportLegId(null); // Close dropdown after selection
                                }}
                                className="text-sm w-[180px] mt-1"
                              >
                                {transportOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </Select>
                            )}
                          </div>

                          {/* Transport details */}
                          <div className="flex items-center gap-4 text-sm text-[var(--text)] flex-wrap">
                            <span className="flex items-center gap-1">
                              {getTransportEmoji(nextEffectiveMode)}
                              <span className="font-medium">{nextEffectiveMode}</span>
                            </span>
                            <span className="text-[var(--text-muted)]">•</span>
                            <span className={nextIsOutdated ? 'text-orange-600' : ''}>
                              {nextIsOutdated ? '--- km *' : `${Math.round(nextTransport.distance)} km`}
                            </span>
                            <span className="text-[var(--text-muted)]">•</span>
                            <span className={nextIsOutdated ? 'text-orange-600' : ''}>
                              {nextIsOutdated ? '--- h *' : `${nextTransport.duration}h`}
                            </span>
                            <span className="text-[var(--text-muted)]">•</span>
                            <span className={nextIsOutdated ? 'text-orange-600' : ''}>
                              {nextIsOutdated ? '--- € *' : `€${Math.round(nextTransport.cost)}`}
                            </span>
                            <span className="text-[var(--text-muted)]">•</span>
                            <span className={nextIsOutdated ? 'text-orange-600' : ''}>
                              {nextIsOutdated ? '--- kg *' : `${Math.round(nextTransport.co2)} kg CO₂`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Add Stop Button (after each stop) */}
                  <div className="flex justify-center mt-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleAddStop(stop.order)}
                      disabled={awaitingApproval || editor.isSubmitting}
                      title={approvalTooltip}
                      className="text-xs px-3 py-1"
                    >
                      <span className="mr-1">➕</span>
                      Add new stop
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Edit Action Bar - only shown in edit/approval mode */}
      {showActionBar && (
        <EditActionBar
          mode={mode}
          awaitingApproval={awaitingApproval}
          isSubmitting={editor.isSubmitting}
          pendingChangesCount={pendingChanges.length}
          hasOriginalJourneyId={!!editor.originalJourney?.id}
          onSubmitChanges={editor.submitChanges}
          onDiscardChanges={handleDiscardChanges}
          onApproveUpdate={handleApproveUpdate}
          onApproveCreateNew={handleApproveCreateNew}
          onApproveCreateUnsaved={handleApproveCreateUnsaved}
        />
      )}
      
      {/* Add Stop Dialog */}
      {addingStopAfter !== null && (
        <StopLocationDialog
          mode="add"
          title="Add New Stop"
          onConfirm={confirmAddStop}
          onCancel={() => setAddingStopAfter(null)}
        />
      )}
      
      {/* Replace Stop Dialog */}
      {replacingStopId !== null && (
        <StopLocationDialog
          mode="replace"
          title="Replace Stop"
          onConfirm={confirmReplaceStop}
          onCancel={() => setReplacingStopId(null)}
        />
      )}
      
      {/* Remove Stop Confirmation */}
      {removingStopId !== null && (() => {
        const stopToRemove: any = stops.find((s: any) => s.id === removingStopId);
        if (!stopToRemove) return null;
        
        return (
          <RemoveStopDialog
            stop={{
              order: stopToRemove.order,
              location: stopToRemove.city || stopToRemove.location || 'Unknown',
              location_country: stopToRemove.country || stopToRemove.location_country || ''
            }}
            onConfirm={confirmRemoveStop}
            onCancel={() => setRemovingStopId(null)}
          />
        );
      })()}
      
      {/* Discard Changes Confirmation */}
      {showDiscardDialog && (
        <ConfirmationDialog
          title="Discard all changes?"
          message="Any unsaved changes will be lost."
          onConfirm={confirmDiscardChanges}
          onCancel={() => setShowDiscardDialog(false)}
          confirmText="Discard"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />
      )}
      
      {/* Notes Dialog */}
      {editor.showNotesDialog && (
        <InfoDialog
          title="About new stops"
          message={editor.notesMessage}
          onClose={editor.closeNotesDialog}
          buttonText="Okay"
        />
      )}
    </div>
  );
};

export default EditableJourney;
